import { useState, useEffect, useCallback } from 'react'
import type { TicketContext } from '@/types/halopsa'
import {
  fetchTicketContext,
  createApiSession,
  hasValidSession,
  getSessionTokenData,
  injectSessionToken,
} from '@/services/api'
import { getTicketIdFromUrl } from '@/lib/utils'

interface UseLiteContextReturn {
  context: TicketContext | null
  loading: boolean
  error: string | null
  refetch: () => void
  ticketId: number | null
}

/**
 * Get member identity from URL parameters (provided by Halo PSA custom tab)
 */
function getMemberFromUrl(): { memberId: string | null; memberName: string | null } {
  const params = new URLSearchParams(window.location.search)
  const memberId = params.get('memberId') || params.get('member_id') || params.get('agentId') || params.get('agent_id') || null
  const memberName = params.get('memberName') || params.get('member_name') || params.get('agentName') || params.get('agent_name') || null
  return { memberId, memberName }
}

export function useLiteContext(): UseLiteContextReturn {
  const [context, setContext] = useState<TicketContext | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [ticketId, setTicketId] = useState<number | null>(null)

  const loadContext = useCallback(async (id: number) => {
    setLoading(true)
    setError(null)

    try {
      console.log(`[useLiteContext] Fetching ticket ${id}`)
      const data = await fetchTicketContext(id)
      setContext(data)
    } catch (err) {
      console.error('[useLiteContext] Error fetching context:', err)
      setError(err instanceof Error ? err.message : 'Failed to load ticket context')
    } finally {
      setLoading(false)
    }
  }, [])

  // Initial load - create session, then get ticket
  useEffect(() => {
    const id = getTicketIdFromUrl()
    const { memberId, memberName } = getMemberFromUrl()

    // Check if this is a popout window — inherit session from parent
    const params = new URLSearchParams(window.location.search)
    const isPopout = params.get('popout') === 'true'

    if (isPopout && window.opener) {
      console.log('[useLiteContext] Popout window detected, requesting session from parent...')
      const handleMessage = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return
        if (event.data?.type === 'halopod-session-response') {
          window.removeEventListener('message', handleMessage)
          injectSessionToken(event.data.token, event.data.expiresAt)
          console.log('[useLiteContext] Session inherited from parent window')
          if (id) {
            setTicketId(id)
            loadContext(id)
          }
        }
      }

      window.addEventListener('message', handleMessage)
      window.opener.postMessage({ type: 'halopod-session-request' }, window.location.origin)

      const timeout = setTimeout(() => {
        window.removeEventListener('message', handleMessage)
        console.warn('[useLiteContext] Popout session request timed out')
        setLoading(false)
        setError('Failed to inherit session from parent window. Please close and reopen.')
      }, 3000)

      return () => {
        clearTimeout(timeout)
        window.removeEventListener('message', handleMessage)
      }
    }

    // Normal flow
    if (id) {
      setTicketId(id)

      const initSession = async () => {
        // Create session if we have a memberId
        if (!hasValidSession() && memberId) {
          try {
            console.log('[useLiteContext] Creating session for member:', memberId)
            await createApiSession({
              memberId,
              memberName: memberName || undefined,
              ticketId: id,
            })
          } catch (sessionErr) {
            console.error('[useLiteContext] Session creation failed:', sessionErr)
            // Fall through to load context with API-key auth
          }
        } else if (!memberId) {
          console.log('[useLiteContext] No memberId — using API-key auth (Halo PSA embedded mode)')
        }

        loadContext(id)
      }

      initSession()
    } else {
      if (import.meta.env.DEV) {
        const mockId = 12345
        setTicketId(mockId)
        loadContext(mockId)
      } else {
        setLoading(false)
        setError('No ticket ID provided. Please open this widget from Halo PSA.')
      }
    }
  }, [loadContext])

  // Listen for session requests from popout windows
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return
      if (event.data?.type === 'halopod-session-request') {
        const tokenData = getSessionTokenData()
        if (tokenData && event.source) {
          ;(event.source as Window).postMessage(
            { type: 'halopod-session-response', ...tokenData },
            event.origin
          )
          console.log('[useLiteContext] Shared session token with popout window')
        }
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [])

  // Listen for postMessage from parent (Halo PSA iframe)
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'ticket_context' && event.data?.ticketId) {
        const id = parseInt(event.data.ticketId, 10)
        if (!isNaN(id) && id !== ticketId) {
          setTicketId(id)
          loadContext(id)
        }
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [ticketId, loadContext])

  const refetch = useCallback(() => {
    if (ticketId) {
      loadContext(ticketId)
    }
  }, [ticketId, loadContext])

  return { context, loading, error, refetch, ticketId }
}
